import { MRT_TableOptions } from "material-react-table";
import { Post } from "src/api/client-api/post/postSchema";
import { SsqSsqHeading, SsqVersion } from "src/api/sure-api/ssq/useGetOneSsqVersion";
import { HeadingsSectionData } from "../QuestionnairesList";
import { HeadingView, QuestionnaireListContext, QuestionnaireListInitialValues } from "./context";
import { useResponsiveBreakpoints } from "@foodpilot/foods";
import { useState, useEffect } from "react";
import { formatDateTime } from "src/utils/formatting";
import { getVisibleHeadings } from "src/utils/ssq";
import { useTranslation } from "react-i18next";
import { useLanguage } from "src/context/LanguageProvider";
import { ScoreService } from "src/services/ScoreService";
import { ExportSubject } from "src/utils/exportNaming";

type Props = {
    ssqVersion?: SsqVersion;
    post?: Post;
    isSsqLoading: boolean;
    isPostLoading: boolean;
    completeQuestionnaireUrl: (ssqSsqHeading: SsqSsqHeading) => string;
    scoresSection: ReactNode;
    displayDateLastModified?: boolean;
    enableTableHead?: boolean;
    muiTableProps?: Omit<MRT_TableOptions<HeadingsSectionData>["muiTableProps"], "columns" | "data">;
    title?: string;
    exportSubject: ExportSubject;

    children: ReactNode;
};
export const QuestionnaireListProvider = (props: Props) => {
    const { children, ...otherProps } = props;
    const { t } = useTranslation();
    const { currentLanguage } = useLanguage();

    const { ssqVersion, post } = otherProps;
    const canAnswerSsq = post?.canAnswerSsq ?? false;

    const { isMobile, isTablet } = useResponsiveBreakpoints();
    const isMobileOrTablet = isMobile || isTablet;

    const [isLockPostModalOpen, setLockPostModalOpen] = useState(false);
    const [view, setView] = useState<HeadingView>("list");

    const [isLocked, setLocked] = useState<boolean>(post?.isLocked ?? false);
    useEffect(() => {
        if (post !== undefined) {
            setLocked(post.isLocked ?? false);
        }
    }, [post]);

    // Vérifier s'il y a réellement des scores à afficher pour ce QSS
    const hasScoresToDisplay = ssqVersion ? ScoreService.getScoreIdsFromSsq(ssqVersion.ssq).length > 0 : false;

    const questionnaireCompleteText =
        canAnswerSsq && !isLocked ?
            post?.completions.ssq.result === 100 ?
                t("questionnaire.modify", "Modify")
            :   t("questionnaire.complete", "Complete")
        :   t("questionnaire.consult", "Consult");

    const ssqSsqHeadings: SsqSsqHeading[] = ssqVersion ? getVisibleHeadings(ssqVersion, post?.hiddenElements) : [];

    const mostRecentTimestamp = resolveMostRecentTimestamp(post?.elements?.map((element) => element.lastChangedAt));
    const lastUpdatePostHeadings =
        mostRecentTimestamp === undefined ? "-" : formatDateTime(new Date(mostRecentTimestamp), currentLanguage);

    const postDate = post?.lastChangedAt == null ? undefined : new Date(Date.parse(post.lastChangedAt));
    const lastUpdatePost = postDate === undefined ? "-" : formatDateTime(postDate, currentLanguage);

    const items: QuestionnaireListInitialValues = {
        ...otherProps,
        title: otherProps.title ?? t("questionnaire"),

        questionnaireCompleteText,
        hasScoresToDisplay,
        ssqSsqHeadings,
        view,
        setView,
        isLocked,
        canViewSsq: post?.canViewSsq,
        canAnswerSsq,
        canValidateSsq: post?.canValidateSsq,
        setLocked,
        isLockPostModalOpen,
        setLockPostModalOpen,
        lastChangedAtDate: lastUpdatePostHeadings,
        lastUpdatePost,
        isMobileOrTablet,
    };
    return <QuestionnaireListContext.Provider value={items}>{props.children}</QuestionnaireListContext.Provider>;
};

export const resolveMostRecentTimestamp = (dates: (string | null | undefined)[] | undefined): number | undefined => {
    if (!dates || dates.length === 0) {
        return undefined;
    }

    const validTimestamps = dates
        .map((date) => (date == null ? null : Date.parse(date)))
        .filter((timestamp): timestamp is number => timestamp !== null && !Number.isNaN(timestamp));

    return validTimestamps.length > 0 ? Math.max(...validTimestamps) : undefined;
};
